import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Plug, ShieldCheck } from "lucide-react";
import { useEffect } from "react";
import { useSearchParams } from "react-router";
import { toast } from "sonner";
import { api } from "../api";
import { ServerCard } from "../components/integrations/server-card";
import { EmptyState, PageHeader, PageSkeleton } from "../components/page";
import { StatusDot } from "../components/status-badge";
import { Alert, AlertDescription } from "../components/ui/alert";
import { Badge } from "../components/ui/badge";

/**
 * MCP manager. The catalog comes from the repo; this page controls whether
 * each server is enabled, how it authenticates, which tools agents may use
 * and which sections receive it.
 */
export function IntegrationsPage() {
  const queryClient = useQueryClient();
  const [params, setParams] = useSearchParams();
  const query = useQuery({ queryKey: ["mcp"], queryFn: api.mcpServers });

  useEffect(() => {
    const connected = params.get("connected");
    const error = params.get("error");
    if (connected) toast.success(`Connected ${connected}`);
    if (error) toast.error("Connection failed", { description: error });
    if (connected || error) {
      queryClient.invalidateQueries({ queryKey: ["mcp"] });
      setParams({}, { replace: true });
    }
  }, [params, setParams, queryClient]);

  if (query.isLoading) return <PageSkeleton />;
  const servers = query.data?.servers ?? [];
  const live = servers.filter((s) => s.connection.enabled && s.connection.authStatus === "connected").length;

  return (
    <>
      <PageHeader
        title="Integrations"
        description="MCP servers the agents can use, on every provider."
        actions={
          servers.length > 0 && (
            <Badge variant="outline" className="h-7 gap-2 px-3">
              <StatusDot tone={live ? "emerald" : "neutral"} />
              {live} of {servers.length} live
            </Badge>
          )
        }
      />
      <Alert>
        <ShieldCheck />
        <AlertDescription>
          <span>
            Switch a tool on and Skynet hands it to every agent that asks for it. Definitions live in{" "}
            <code className="rounded bg-muted px-1 py-0.5 font-mono text-xs">packages/agents/src/mcp.ts</code>
            ; credentials stay in AWS Secrets Manager and never leave the backend.
          </span>
        </AlertDescription>
      </Alert>
      {servers.length === 0 ? (
        <EmptyState
          icon={Plug}
          title="No integrations yet"
          description="No MCP servers are defined in the catalog."
        />
      ) : (
        <div className="grid gap-6 @5xl/main:grid-cols-2">
          {servers.map((view) => (
            <ServerCard key={view.server.id} view={view} />
          ))}
        </div>
      )}
    </>
  );
}
